import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  X,
  Search,
  Folder,
  FileText,
  Users,
  Building,
  Briefcase,
  FolderOpen,
  ChevronRight,
  ChevronLeft,
  Save,
  Loader,
  Check
} from 'lucide-react';

/**
 * SaveAttachmentToCRM - Enregistrer une pièce jointe dans le CRM
 * Permet de sauvegarder un fichier reçu par email vers :
 * - Documents d'un Client
 * - Documents d'un Courtier
 * - Documents d'une Compagnie
 * - Documents Cabinet
 */
const SaveAttachmentToCRM = ({ 
  isOpen, 
  onClose, 
  attachment,
  emailId,
  attachmentIndex,
  backendUrl,
  authToken,
  onSuccess
}) => {
  const { t } = useTranslation('admin');
  // Source active
  const [activeSource, setActiveSource] = useState('clients');
  
  // Recherche
  const [searchEntityTerm, setSearchEntityTerm] = useState('');
  
  // Données
  const [clients, setClients] = useState([]);
  const [courtiers, setCourtiers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [folders, setFolders] = useState([]);
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const ITEMS_PER_PAGE = 15;
  
  // Sélection
  const [selectedEntity, setSelectedEntity] = useState(null);
  const [selectedFolder, setSelectedFolder] = useState(null);
  
  // Loading & état
  const [loadingEntities, setLoadingEntities] = useState(false);
  const [loadingFolders, setLoadingFolders] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  // Nom personnalisé (modifiable avant enregistrement)
  const [customFilename, setCustomFilename] = useState('');

  // Sources disponibles
  const sources = [
    { id: 'clients', label: t('docs.save.client'), icon: Users, color: 'blue' },
    { id: 'courtiers', label: t('docs.save.courtier'), icon: Briefcase, color: 'green' },
    { id: 'companies', label: t('docs.save.company'), icon: Building, color: 'purple' },
    { id: 'cabinet', label: t('docs.save.cabinet'), icon: FolderOpen, color: 'amber' }
  ];

  // Reset à l'ouverture
  useEffect(() => {
    if (isOpen) {
      setSaved(false);
      setSelectedEntity(null);
      setSelectedFolder(null);
      setCurrentPage(1);
      setCustomFilename(decodeMimeFilename(attachment?.original_filename || 'document'));
      loadEntities(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, activeSource]);

  // Charger quand la page change
  useEffect(() => {
    if (isOpen && activeSource === 'clients') {
      loadEntities(currentPage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  const loadEntities = async (page = 1) => {
    if (activeSource === 'cabinet') {
      // Pour cabinet, charger directement les dossiers
      loadCabinetFolders();
      return;
    }
    
    setLoadingEntities(true);
    setSelectedEntity(null);
    setFolders([]);
    
    try {
      let url = '';
      switch (activeSource) {
        case 'clients':
          url = `${backendUrl}/api/admin/users?role=client&limit=${ITEMS_PER_PAGE}&page=${page}`;
          if (searchEntityTerm && searchEntityTerm.length >= 2) {
            url += `&search=${encodeURIComponent(searchEntityTerm)}`;
          }
          break;
        case 'courtiers':
          url = `${backendUrl}/api/admin/courtiers`;
          break;
        case 'companies':
          url = `${backendUrl}/api/admin/contact-directory/companies`;
          break;
        default:
          return;
      }

      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${authToken}` }
      });

      if (response.ok) {
        const data = await response.json();
        
        switch (activeSource) {
          case 'clients':
            setClients(data.users || data.items || data || []);
            if (data.pagination) {
              setTotalPages(data.pagination.total_pages || 1);
              setTotalCount(data.pagination.total || 0);
            }
            break;
          case 'courtiers':
            setCourtiers(data.items || data || []);
            break;
          case 'companies':
            setCompanies(data || []);
            break;
          default:
            break;
        }
      }
    } catch (error) {
      console.error('Erreur chargement entités:', error);
    } finally {
      setLoadingEntities(false);
    }
  };

  const loadCabinetFolders = async () => {
    setLoadingFolders(true);
    try {
      const response = await fetch(`${backendUrl}/api/admin/cabinet/folders`, {
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      if (response.ok) {
        const data = await response.json();
        setFolders(data || []);
      }
    } catch (error) {
      console.error('Erreur chargement dossiers cabinet:', error);
    } finally {
      setLoadingFolders(false);
    }
  };

  const loadEntityFolders = async (entity) => {
    setSelectedEntity(entity);
    setSelectedFolder(null);
    setLoadingFolders(true);
    
    try {
      let url = '';
      switch (activeSource) {
        case 'clients':
          url = `${backendUrl}/api/client/${entity.id}/folders`;
          break;
        case 'courtiers':
          url = `${backendUrl}/api/courtier/${entity.id}/folders`;
          break;
        case 'companies':
          url = `${backendUrl}/api/admin/contact-directory/companies/${entity.id}/folders`;
          break;
        default:
          return;
      }

      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${authToken}` }
      });

      if (response.ok) {
        const data = await response.json();
        setFolders(data || []);
      } else {
        setFolders([]);
      }
    } catch (error) {
      console.error('Erreur chargement dossiers:', error);
      setFolders([]);
    } finally {
      setLoadingFolders(false);
    }
  };

  // Nom de l'entité
  const getEntityName = (entity) => {
    if (entity.first_name && entity.last_name) {
      return `${entity.first_name} ${entity.last_name}`;
    }
    return entity.name || entity.company || 'Sans nom';
  };

  // Obtenir les entités filtrées
  const getFilteredEntities = () => {
    let entities = [];
    switch (activeSource) {
      case 'clients':
        entities = clients;
        break;
      case 'courtiers':
        entities = courtiers;
        break;
      case 'companies':
        entities = companies;
        break;
      default:
        return [];
    }

    // Pour les courtiers et compagnies, filtrer côté client
    if (activeSource !== 'clients' && searchEntityTerm) {
      const term = searchEntityTerm.toLowerCase();
      return entities.filter(e => {
        const name = getEntityName(e).toLowerCase();
        const email = (e.email || '').toLowerCase();
        return name.includes(term) || email.includes(term);
      });
    }

    return entities;
  };

  // Décoder les noms de fichiers MIME (RFC 2047)
  const decodeMimeFilename = (filename) => {
    if (!filename) return 'document';
    
    // Si le nom contient l'encodage MIME =?UTF-8?Q?...?= ou =?UTF-8?B?...?=
    if (filename.includes('=?') && filename.includes('?=')) {
      try {
        // Remplacer les encodages Quoted-Printable
        let decoded = filename
          .replace(/=\?UTF-8\?Q\?/gi, '')
          .replace(/\?=\s*=\?UTF-8\?Q\?/gi, '') // Jointure entre parties
          .replace(/\?=/g, '')
          .replace(/=([0-9A-Fa-f]{2})/g, (match, hex) => String.fromCharCode(parseInt(hex, 16)))
          .replace(/_/g, ' ')
          .trim();
        
        return decoded || 'document';
      } catch (e) {
        // Fallback: nettoyer le nom
        return filename.replace(/=\?[^?]*\?[QB]\?/gi, '').replace(/\?=/g, '').replace(/_/g, ' ').trim() || 'document';
      }
    }
    
    return filename;
  };

  // Sauvegarder la pièce jointe
  const handleSave = async () => {
    setSaving(true);
    
    try {
      // Utiliser le nom personnalisé (modifié par l'utilisateur ou décodé automatiquement)
      const cleanFilename = (customFilename || '').trim() || decodeMimeFilename(attachment?.original_filename || 'document');
      
      // 1. Télécharger la pièce jointe depuis l'email
      const downloadUrl = `${backendUrl}/api/admin/mail/attachment/${emailId}/${attachmentIndex}`;
      
      const downloadResponse = await fetch(downloadUrl, {
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      
      
      if (!downloadResponse.ok) {
        const errorText = await downloadResponse.text();
        console.error('Download error:', errorText);
        throw new Error(t('docs.save.downloadError'));
      }
      
      const blob = await downloadResponse.blob();
      
      const file = new File([blob], cleanFilename, { 
        type: attachment?.content_type || attachment?.mime_type || blob.type || 'application/octet-stream' 
      });
      
      // 2. Uploader vers la destination choisie
      const formData = new FormData();
      
      // Le nom du champ dépend de la route (certaines utilisent 'file', d'autres 'files')
      let uploadUrl = '';
      switch (activeSource) {
        case 'clients':
          formData.append('file', file);
          uploadUrl = `${backendUrl}/api/client/${selectedEntity.id}/documents/upload`;
          break;
        case 'courtiers':
          formData.append('file', file);
          uploadUrl = `${backendUrl}/api/courtier/${selectedEntity.id}/documents/upload`;
          break;
        case 'companies':
          formData.append('files', file); // Cette route utilise 'files' au pluriel
          uploadUrl = `${backendUrl}/api/admin/contact-directory/companies/${selectedEntity.id}/documents/upload`;
          break;
        case 'cabinet':
          formData.append('files', file);
          uploadUrl = `${backendUrl}/api/admin/cabinet/documents`;
          break;
        default:
          throw new Error(t('docs.save.sourceNotSupported'));
      }
      
      formData.append('document_type', 'admin_only');
      
      if (selectedFolder) {
        formData.append('folder_id', selectedFolder.id);
      }
      
      
      const uploadResponse = await fetch(uploadUrl, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${authToken}` },
        body: formData
      });
      
      // Lire la réponse pour debug
      const responseText = await uploadResponse.text();
      
      if (uploadResponse.ok) {
        setSaved(true);
        if (onSuccess) {
          const destName = activeSource === 'cabinet' 
            ? t('docs.attach.cabinetDocs') 
            : getEntityName(selectedEntity);
          onSuccess(t('docs.save.savedIn', { dest: destName }));
        }
        setTimeout(() => onClose(), 1500);
      } else {
        // Afficher le détail de l'erreur
        let errorDetail = t('docs.save.saveError');
        try {
          const errorJson = JSON.parse(responseText);
          if (typeof errorJson.detail === 'string') {
            errorDetail = errorJson.detail;
          } else if (typeof errorJson.detail === 'object') {
            errorDetail = JSON.stringify(errorJson.detail);
          } else if (errorJson.message) {
            errorDetail = errorJson.message;
          }
        } catch (e) {
          errorDetail = responseText || errorDetail;
        }
        console.error('Upload error details:', responseText);
        throw new Error(errorDetail);
      }
    } catch (error) {
      console.error('Erreur sauvegarde complète:', error);
      const errorMsg = error.message || String(error);
      alert(t('docs.toast.errorPrefix', { msg: errorMsg }));
    } finally {
      setSaving(false);
    }
  };

  // Peut sauvegarder ?
  const canSave = () => {
    if (activeSource === 'cabinet') return true;
    return selectedEntity !== null;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[1400] p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-slate-200">
          <div className="flex-1 min-w-0">
            <h2 className="text-lg font-semibold text-slate-900">
              {t('docs.save.title')}
            </h2>
            <div className="flex items-center gap-2 mt-1">
              <input
                type="text"
                value={customFilename}
                onChange={(e) => setCustomFilename(e.target.value)}
                className="flex-1 text-sm border border-slate-300 rounded-lg px-2 py-1 text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:border-transparent"
                placeholder={t('docs.save.filenamePlaceholder', { defaultValue: 'Nom du fichier...' })}
                title={t('docs.save.filenameEditHint', { defaultValue: 'Modifier le nom du fichier avant enregistrement' })}
              />
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 flex overflow-hidden">
          {/* Sidebar - Sources */}
          <div className="w-44 border-r border-slate-200 bg-slate-50 p-3 flex-shrink-0">
            <p className="text-xs font-medium text-slate-500 uppercase mb-2 px-2">{t('docs.save.destination')}</p>
            <div className="space-y-1">
              {sources.map(source => {
                const Icon = source.icon;
                const isActive = activeSource === source.id;
                return (
                  <button
                    key={source.id}
                    onClick={() => {
                      setActiveSource(source.id);
                      setSelectedEntity(null);
                      setSelectedFolder(null);
                      setCurrentPage(1);
                    }}
                    className={`w-full flex items-center px-3 py-2 rounded-lg text-sm transition-colors ${
                      isActive 
                        ? 'bg-red-100 text-red-700 font-medium'
                        : 'text-slate-600 hover:bg-slate-100'
                    }`}
                  >
                    <Icon className={`w-4 h-4 mr-2 ${isActive ? 'text-red-600' : 'text-slate-400'}`} />
                    {source.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Main Content */}
          <div className="flex-1 flex overflow-hidden">
            {/* Liste des entités (sauf pour cabinet) */}
            {activeSource !== 'cabinet' && (
              <div className="w-64 border-r border-slate-200 flex flex-col">
                {/* Recherche */}
                <div className="p-3 border-b border-slate-200">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <input
                      type="text"
                      placeholder={t('docs.save.search')}
                      value={searchEntityTerm}
                      onChange={(e) => {
                        setSearchEntityTerm(e.target.value);
                        if (activeSource === 'clients') {
                          if (e.target.value.length >= 2 || e.target.value.length === 0) {
                            setCurrentPage(1);
                            loadEntities(1);
                          }
                        }
                      }}
                      className="w-full pl-9 pr-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                    />
                  </div>
                  {activeSource === 'clients' && totalCount > 0 && (
                    <p className="text-xs text-slate-500 mt-2 text-center">
                      {t('docs.save.clientsCount', { count: totalCount })}
                    </p>
                  )}
                </div>

                {/* Liste */}
                <div className="flex-1 overflow-y-auto p-2">
                  {loadingEntities ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader className="w-5 h-5 text-slate-400 animate-spin" />
                    </div>
                  ) : getFilteredEntities().length === 0 ? (
                    <p className="text-sm text-slate-500 text-center py-8">{t('docs.attach.noResult')}</p>
                  ) : (
                    <div className="space-y-1">
                      {getFilteredEntities().map(entity => (
                        <button
                          key={entity.id}
                          onClick={() => loadEntityFolders(entity)}
                          className={`w-full flex items-center px-3 py-2 rounded-lg text-left transition-colors ${
                            selectedEntity?.id === entity.id
                              ? 'bg-red-50 border border-red-200'
                              : 'hover:bg-slate-100'
                          }`}
                        >
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-slate-900 truncate">
                              {getEntityName(entity)}
                            </p>
                            {entity.email && (
                              <p className="text-xs text-slate-500 truncate">{entity.email}</p>
                            )}
                          </div>
                          {selectedEntity?.id === entity.id && (
                            <Check className="w-4 h-4 text-red-600 flex-shrink-0" />
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Pagination clients */}
                {activeSource === 'clients' && totalPages > 1 && (
                  <div className="p-2 border-t border-slate-200 flex items-center justify-between">
                    <button
                      onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                      className="p-1.5 text-slate-600 hover:bg-slate-100 rounded disabled:opacity-40"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </button>
                    <span className="text-xs text-slate-600">{currentPage} / {totalPages}</span>
                    <button
                      onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages}
                      className="p-1.5 text-slate-600 hover:bg-slate-100 rounded disabled:opacity-40"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Dossiers (optionnel) */}
            <div className="flex-1 flex flex-col p-4">
              <h4 className="text-sm font-medium text-slate-700 mb-3">
                {activeSource === 'cabinet' ? t('docs.save.cabinetFolders') : t('docs.save.folderOptional')}
              </h4>

              {loadingFolders ? (
                <div className="flex items-center justify-center py-8">
                  <Loader className="w-5 h-5 text-slate-400 animate-spin" />
                </div>
              ) : activeSource !== 'cabinet' && !selectedEntity ? (
                <div className="flex-1 flex items-center justify-center text-slate-400">
                  <p className="text-sm">{activeSource === 'clients' ? t('docs.save.selectFirstClient') : activeSource === 'courtiers' ? t('docs.save.selectFirstCourtier') : t('docs.save.selectFirstCompany')}</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {/* Option racine */}
                  <button
                    onClick={() => setSelectedFolder(null)}
                    className={`w-full flex items-center p-3 rounded-lg border transition-colors ${
                      selectedFolder === null
                        ? 'border-red-500 bg-red-50'
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <Folder className={`w-5 h-5 mr-3 ${selectedFolder === null ? 'text-red-500' : 'text-slate-400'}`} />
                    <span className="text-sm font-medium">{t('docs.save.rootNoFolder')}</span>
                    {selectedFolder === null && (
                      <Check className="w-4 h-4 text-red-600 ml-auto" />
                    )}
                  </button>

                  {folders.map(folder => (
                    <button
                      key={folder.id}
                      onClick={() => setSelectedFolder(folder)}
                      className={`w-full flex items-center p-3 rounded-lg border transition-colors ${
                        selectedFolder?.id === folder.id
                          ? 'border-red-500 bg-red-50'
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      <Folder className={`w-5 h-5 mr-3 ${selectedFolder?.id === folder.id ? 'text-red-500' : 'text-yellow-500'}`} />
                      <span className="text-sm font-medium truncate">{folder.name}</span>
                      {selectedFolder?.id === folder.id && (
                        <Check className="w-4 h-4 text-red-600 ml-auto" />
                      )}
                    </button>
                  ))}

                  {folders.length === 0 && (activeSource === 'cabinet' || selectedEntity) && (
                    <p className="text-sm text-slate-500 text-center py-4">
                      {t('docs.save.noFolder')}
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-4 border-t border-slate-200 bg-slate-50">
          <div className="text-sm text-slate-600">
            {saved ? (
              <span className="text-green-600 flex items-center">
                <Check className="w-4 h-4 mr-1" /> {t('docs.save.saved')}
              </span>
            ) : selectedEntity ? (
              <span>{t('docs.save.toward')} <strong>{getEntityName(selectedEntity)}</strong>{selectedFolder ? ` / ${selectedFolder.name}` : ''}</span>
            ) : activeSource === 'cabinet' ? (
              <span>{t('docs.save.toward')} <strong>{t('docs.attach.cabinetDocs')}</strong>{selectedFolder ? ` / ${selectedFolder.name}` : ''}</span>
            ) : (
              <span className="text-slate-400">{t('docs.save.selectDestination')}</span>
            )}
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50"
            >
              {t('common.cancel')}
            </button>
            <button
              onClick={handleSave}
              disabled={!canSave() || saving || saved}
              className="px-4 py-2 text-sm text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            >
              {saving ? (
                <Loader className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              {saving ? t('docs.save.saving') : t('docs.save.save')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SaveAttachmentToCRM;
